import { useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { RegistroHero, FormSection, Field, CvUpload, ConsentBox } from "@/components/registro/RegistroParts";
import { CENTROS, CURSOS, TITULACION_LIST, TITULACIONES } from "@/lib/demo/seed";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n/i18n";

const EMPTY = { firstName: "", lastName: "", dni: "", email: "", phone: "", centro: "", titulacion: "", curso: "", linkedin: "", password: "", password2: "" };
const REQUIRED = ["firstName", "lastName", "dni", "email", "phone", "centro", "titulacion", "curso", "password", "password2"];
const MAX_CV = 5 * 1024 * 1024;

export const SelectField = ({ id, label, value, onChange, options, error }) => {
  const { t } = useI18n();
  return (
  <div className="space-y-2">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t(label)}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-12 rounded-xl ${error ? "border-rose-400" : ""}`} data-testid={`registro-select-${id}`}><SelectValue placeholder={t("Selecciona…")} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o} data-testid={`registro-option-${id}-${o}`}>{t(o)}</SelectItem>)}</SelectContent>
    </Select>
    {error && <p className="text-xs font-medium text-rose-600" data-testid={`registro-error-${id}`}>{t(error)}</p>}
  </div>
  );
};

export default function Registro() {
  const nav = useNavigate();
  const { user, startSession } = useAuth();
  const { t } = useI18n();
  const formRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  if (user?.role === "participant" && !submitting) return <Navigate to="/credencial" replace />;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v, ...(k === "titulacion" ? { centro: TITULACIONES[v] } : {}) }));
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const onFile = (file) => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error(t("El currículum debe estar en formato PDF")); return; }
    if (file.size > MAX_CV) { toast.error(t("El CV supera el tamaño máximo (5 MB)")); return; }
    setCv({ name: file.name, file });
    setErrors((e) => ({ ...e, cv: undefined }));
  };


  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    REQUIRED.forEach((k) => { if (!form[k].trim()) errs[k] = "Campo obligatorio"; });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Introduce un email válido";
    if (form.password && (form.password.length < 8 || !/\d/.test(form.password) || !/[a-zA-Z]/.test(form.password))) errs.password = "Mínimo 8 caracteres, con letras y números.";
    if (form.password2 && form.password !== form.password2) errs.password2 = "Las contraseñas no coinciden";
    if (!cv) errs.cv = "Sube tu currículum en PDF";
    if (!consent) errs.consent = "Debes aceptar las condiciones para completar el registro";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(t("Revisa los campos marcados")); return; }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => k !== "password2" && fd.append(k, v));
    fd.append("consent", "true");
    fd.append("cv", cv.file, cv.name);
    try {
      const { data } = await api.post("/participants/register", fd);
      startSession(data);
      nav("/credencial");
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields);
      toast.error(t(message));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="registro-page">
      <SiteHeader />
      <RegistroHero onCta={scrollToForm} />
      <div ref={formRef} className="mx-auto grid max-w-6xl scroll-mt-20 gap-10 px-5 py-14 lg:grid-cols-[280px_1fr] lg:px-10">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Inscripción")}</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">{t("Crea tu credencial")}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t("Un único registro. Recibirás tu QR personal al instante.")}</p>
          <Link to="/login?next=/credencial" className="mt-4 block text-sm font-semibold text-upv hover:underline" data-testid="registro-login-link">{t("¿Ya estás registrado? Inicia sesión")}</Link>
        </aside>
        <form onSubmit={submit} noValidate className="rounded-[28px] border border-slate-200 bg-white px-6 shadow-sm sm:px-10" data-testid="registro-form">
          <FormSection n={1} title={t("Datos personales")} testId="registro-section-personal">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="firstName" label="Nombre" value={form.firstName} onChange={set("firstName")} error={errors.firstName} />
              <Field id="lastName" label="Apellidos" value={form.lastName} onChange={set("lastName")} error={errors.lastName} />
              <Field id="dni" label="DNI / NIE" value={form.dni} onChange={set("dni")} error={errors.dni} />
              <Field id="phone" label="Teléfono" type="tel" value={form.phone} onChange={set("phone")} error={errors.phone} />
              <div className="sm:col-span-2"><Field id="email" label="Email" type="email" autoComplete="email" value={form.email} onChange={set("email")} error={errors.email} /></div>
              <Field id="password" label="Contraseña" type="password" autoComplete="new-password" value={form.password} onChange={set("password")} error={errors.password} />
              <Field id="password2" label="Repite la contraseña" type="password" autoComplete="new-password" value={form.password2} onChange={set("password2")} error={errors.password2} />
              <p className="-mt-2 text-xs text-slate-400 sm:col-span-2">{t("Con tu email y contraseña podrás recuperar tu credencial en cualquier momento.")}</p>
            </div>
          </FormSection>
          <FormSection n={2} title={t("Información académica")} testId="registro-section-academic">
            <div className="grid gap-5 sm:grid-cols-3">
              <div className="sm:col-span-3"><SelectField id="titulacion" label="Titulación" value={form.titulacion} onChange={set("titulacion")} options={TITULACION_LIST} error={errors.titulacion} /></div>
              <SelectField id="centro" label="Centro" value={form.centro} onChange={set("centro")} options={CENTROS} error={errors.centro} />
              <div className="sm:col-span-2"><SelectField id="curso" label="Curso" value={form.curso} onChange={set("curso")} options={CURSOS} error={errors.curso} /></div>
            </div>
          </FormSection>
          <FormSection n={3} title={t("Perfil profesional")} testId="registro-section-profile">
            <Field id="linkedin" label="LinkedIn" optional placeholder="linkedin.com/in/tu-perfil" value={form.linkedin} onChange={set("linkedin")} />
          </FormSection>
          <FormSection n={4} title={t("Currículum")} testId="registro-section-cv">
            <CvUpload cv={cv} onFile={onFile} error={errors.cv} />
          </FormSection>
          <FormSection n={5} title={t("Consentimiento")} testId="registro-section-consent">
            <ConsentBox checked={consent} onChange={setConsent} error={errors.consent} />
            <button type="submit" disabled={submitting} className="mt-8 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display text-base font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-80" data-testid="registro-submit-button">
              {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("Generando tu credencial…")}</> : t("COMPLETAR REGISTRO")}
            </button>
          </FormSection>
        </form>
      </div>
      <PageFooter />
    </div>
  );
}
